import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../hooks/useApi';
import TermSelector from '../common/TermSelector';
import PremiumTooltip from '../common/PremiumTooltip';
import {
    ChevronDownIcon,
    ChevronUpIcon,
    ChevronUpDownIcon,
    TrophyIcon,
    ExclamationTriangleIcon,
    MagnifyingGlassIcon,
    ScaleIcon,
    AcademicCapIcon,
    ArrowPathIcon,
    CheckCircleIcon,
    ExclamationCircleIcon,
    EyeIcon
} from '@heroicons/react/24/outline';

const ScholarStandingRoster = ({
    scope = 'teacher', // 'teacher' | 'hod' | 'course'
    courseId = null,
    gradeId = null,
    streamId = null,
    yearId = null,
    title = null,
    subtitle = null,
    hideStats = false,
    selectedFilterProp = null,
    selectedTermProp = null,
    onTermChangeProp = null,
    hideTermSelector = false
}) => {
    const navigate = useNavigate();
    const { get } = useApi();
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' }); // Default: Scholar A-Z
    const [internalFilter, setInternalFilter] = useState('all');
    const [expandedStudentId, setExpandedStudentId] = useState(null);
    const [internalTerm, setInternalTerm] = useState('');

    const selectedFilter = selectedFilterProp !== null ? selectedFilterProp : internalFilter;
    const selectedTerm = selectedTermProp !== null ? selectedTermProp : internalTerm;
    const handleTermChange = onTermChangeProp || setInternalTerm;

    // Fetch Academic Terms for Term-based standings
    const { data: terms = [] } = useQuery({
        queryKey: ['academic-terms'],
        queryFn: () => get('/api/academic-terms/'),
        staleTime: 60 * 60 * 1000,
    });

    // Default to active term
    React.useEffect(() => {
        if (terms.length > 0 && !selectedTerm) {
            const current = terms.find(t => t.is_current);
            if (current) handleTermChange(String(current.id));
        }
    }, [terms, selectedTerm, handleTermChange]);

    // Fetch Roster Standings from API
    const {
        data: standings = [],
        isLoading,
        isFetching,
        refetch
    } = useQuery({
        queryKey: ['discipline-roster-standing', scope, courseId, gradeId, streamId, yearId, selectedTerm],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (scope) params.append('scope', scope);
            if (courseId) params.append('course_id', courseId);
            if (gradeId) params.append('grade_id', gradeId);
            if (streamId) params.append('stream_id', streamId);
            if (yearId) params.append('year_id', yearId);
            if (selectedTerm) params.append('term_id', selectedTerm);

            const res = await get(`/api/discipline/records/roster-standing/?${params.toString()}`);
            return Array.isArray(res) ? res : [];
        },
        staleTime: 30 * 1000,
    });

    // Toggle expansion for a scholar row
    const toggleExpand = (studentId) => {
        setExpandedStudentId(prev => prev === studentId ? null : studentId);
    };

    // Aggregate statistics
    const stats = useMemo(() => {
        const totalScholars = standings.length;
        const totalMerits = standings.reduce((acc, s) => acc + (s.total_merits || 0), 0);
        const totalDemerits = standings.reduce((acc, s) => acc + (s.total_demerits || 0), 0);
        const netPoints = totalMerits - totalDemerits;
        const exemplaryCount = standings.filter(s => s.status === 'Exemplary').length;
        const sanctionCount = standings.filter(s => ['Detention', 'Suspension', 'Expelled'].includes(s.status)).length;

        return { totalScholars, totalMerits, totalDemerits, netPoints, exemplaryCount, watchCount: sanctionCount };
    }, [standings]);

    const handleSort = (key) => {
        setSortConfig(prev => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: key === 'name' ? 'asc' : 'desc' };
        });
    };

    // Filter, Search & Interactive Column Sorting
    const filteredStandings = useMemo(() => {
        const filtered = standings.filter(item => {
            const matchesSearch =
                item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.student_adm?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.grade?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.stream?.toLowerCase().includes(searchTerm.toLowerCase());

            if (!matchesSearch) return false;

            if (selectedFilter === 'exemplary') return item.status === 'Exemplary';
            if (selectedFilter === 'good') return item.status === 'Good Standing';
            if (selectedFilter === 'detention') return item.status === 'Detention';
            if (selectedFilter === 'suspension') return item.status === 'Suspension';
            if (selectedFilter === 'expelled') return item.status === 'Expelled';

            return true;
        });

        return filtered.sort((a, b) => {
            if (sortConfig.key === 'name') {
                const valA = (a.name || '').toLowerCase();
                const valB = (b.name || '').toLowerCase();
                return sortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
            } else if (sortConfig.key === 'merits') {
                const valA = a.total_merits || 0;
                const valB = b.total_merits || 0;
                return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
            } else if (sortConfig.key === 'demerits') {
                const valA = a.total_demerits || 0;
                const valB = b.total_demerits || 0;
                return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
            } else if (sortConfig.key === 'net') {
                const valA = (a.total_merits || 0) - (a.total_demerits || 0);
                const valB = (b.total_merits || 0) - (b.total_demerits || 0);
                return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
            }
            return 0;
        });
    }, [standings, searchTerm, selectedFilter, sortConfig]);

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Exemplary':
                return (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200/80 rounded-full text-[10px] font-black uppercase tracking-wider">
                        <TrophyIcon className="w-3.5 h-3.5 text-emerald-600" />
                        Exemplary
                    </span>
                );
            case 'Good Standing':
                return (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-50 text-indigo-700 border border-indigo-200/80 rounded-full text-[10px] font-black uppercase tracking-wider">
                        <CheckCircleIcon className="w-3.5 h-3.5 text-indigo-600" />
                        Good Standing
                    </span>
                );
            case 'Detention':
                return (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-700 border border-amber-200/80 rounded-full text-[10px] font-black uppercase tracking-wider">
                        <ExclamationTriangleIcon className="w-3.5 h-3.5 text-amber-600" />
                        Detention
                    </span>
                );
            case 'Suspension':
                return (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-rose-50 text-rose-700 border border-rose-200/80 rounded-full text-[10px] font-black uppercase tracking-wider">
                        <ExclamationCircleIcon className="w-3.5 h-3.5 text-rose-600" />
                        Suspension
                    </span>
                );
            case 'Expelled':
                return (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-100 text-red-900 border border-red-300 rounded-full text-[10px] font-black uppercase tracking-wider">
                        <ExclamationCircleIcon className="w-3.5 h-3.5 text-red-700" />
                        Expelled
                    </span>
                );
            default:
                return (
                    <span className="inline-flex items-center px-2.5 py-1 bg-slate-100 text-slate-600 rounded-full text-[10px] font-bold">
                        {status}
                    </span>
                );
        }
    };

    const renderSortHeader = (label, sortKey, align = 'left') => {
        const isActive = sortConfig.key === sortKey;
        return (
            <th
                onClick={(e) => {
                    e.stopPropagation();
                    handleSort(sortKey);
                }}
                className={`py-4 px-6 select-none cursor-pointer transition-colors group ${
                    align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left'
                } ${isActive ? 'text-[#18216D]' : 'hover:text-[#18216D]'}`}
            >
                <div className={`inline-flex items-center gap-1.5 ${align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : 'justify-start'}`}>
                    <span>{label}</span>
                    <span className={`transition-colors ${isActive ? 'text-[#18216D]' : 'text-slate-300 group-hover:text-slate-500'}`}>
                        {isActive ? (
                            sortConfig.direction === 'asc' ? (
                                <ChevronUpIcon className="w-3.5 h-3.5 stroke-[2.5]" />
                            ) : (
                                <ChevronDownIcon className="w-3.5 h-3.5 stroke-[2.5]" />
                            )
                        ) : (
                            <ChevronUpDownIcon className="w-3.5 h-3.5 stroke-[2]" />
                        )}
                    </span>
                </div>
            </th>
        );
    };

    return (
        <div className="space-y-6 animate-fadeIn">
            {/* Top Stat Summary Cards (Hidden when embedded in pages that already display these metrics) */}
            {!hideStats && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white p-5 rounded-[1.8rem] border border-slate-100 shadow-sm relative overflow-hidden group">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Enrolled Scholars</p>
                                <h3 className="text-2xl font-black text-[#18216D] mt-1">{stats.totalScholars}</h3>
                            </div>
                            <div className="w-11 h-11 rounded-2xl bg-indigo-50 text-[#18216D] flex items-center justify-center font-bold">
                                <AcademicCapIcon className="w-6 h-6" />
                            </div>
                        </div>
                        <div className="mt-3 text-[10px] font-black text-slate-400 uppercase tracking-wider">
                            Active behavioral roster
                        </div>
                    </div>

                    <div className="bg-white p-5 rounded-[1.8rem] border border-emerald-100/60 shadow-sm relative overflow-hidden group">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Merits Awarded</p>
                                <h3 className="text-2xl font-black text-emerald-600 mt-1">+{stats.totalMerits}</h3>
                            </div>
                            <div className="w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
                                <TrophyIcon className="w-6 h-6 text-[#FFC425]" />
                            </div>
                        </div>
                        <div className="mt-3 text-[10px] font-black text-emerald-600 uppercase tracking-wider">
                            {stats.exemplaryCount} Exemplary Scholars
                        </div>
                    </div>

                    <div className="bg-white p-5 rounded-[1.8rem] border border-rose-100/60 shadow-sm relative overflow-hidden group">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[10px] font-black text-rose-600 uppercase tracking-widest">Demerits Incurred</p>
                                <h3 className="text-2xl font-black text-rose-600 mt-1">-{stats.totalDemerits}</h3>
                            </div>
                            <div className="w-11 h-11 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center font-bold">
                                <ExclamationTriangleIcon className="w-6 h-6" />
                            </div>
                        </div>
                        <div className="mt-3 text-[10px] font-black text-rose-600 uppercase tracking-wider">
                            {stats.watchCount} Under Watch / Critical
                        </div>
                    </div>

                    <div className="bg-white p-5 rounded-[1.8rem] border border-slate-100 shadow-sm relative overflow-hidden group">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Net Department Standing</p>
                                <h3 className={`text-2xl font-black mt-1 ${stats.netPoints >= 0 ? 'text-[#18216D]' : 'text-rose-600'}`}>
                                    {stats.netPoints > 0 ? `+${stats.netPoints}` : stats.netPoints}
                                </h3>
                            </div>
                            <div className="w-11 h-11 rounded-2xl bg-[#FFC425]/20 text-[#18216D] flex items-center justify-center font-bold">
                                <ScaleIcon className="w-6 h-6" />
                            </div>
                        </div>
                        <div className="mt-3 text-[10px] font-black text-slate-400 uppercase tracking-wider">
                            Aggregated Net Merit Score
                        </div>
                    </div>
                </div>
            )}

            {/* Controls, Filters & Search */}
            <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h2 className="text-lg font-black text-[#18216D] tracking-tight uppercase">
                            {title || 'Character Roster'}
                        </h2>
                        <p className="text-xs font-medium text-slate-500 mt-0.5">
                            Displaying <span className="font-bold text-[#18216D]">{filteredStandings.length}</span> {courseId ? 'scholars enrolled in this class.' : 'scholars enrolled across your assigned classes with subject drilldowns.'}
                            {selectedFilter !== 'all' && (
                                <span className="ml-2 px-2.5 py-0.5 bg-indigo-50 text-[#18216D] border border-indigo-100 rounded-lg text-[10px] font-black uppercase tracking-wider">
                                    Standing: {selectedFilter.replace('_', ' ')}
                                </span>
                            )}
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        {!hideTermSelector && terms.length > 0 && (
                            <div className="w-48">
                                <TermSelector
                                    terms={terms}
                                    selectedTerm={selectedTerm}
                                    onTermChange={handleTermChange}
                                    isLoading={isFetching}
                                />
                            </div>
                        )}
                        {/* Search Input relocated directly to the top-right */}
                        <div className="relative w-full sm:w-72">
                            <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search scholar or adm..."
                                className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200/80 rounded-xl text-xs font-bold text-[#18216D] outline-none focus:bg-white focus:border-[#18216D] transition-all"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Interactive Table */}
            <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden">
                {isLoading ? (
                    <div className="py-20 text-center space-y-3">
                        <div className="w-10 h-10 border-4 border-[#18216D] border-t-transparent rounded-full animate-spin mx-auto" />
                        <p className="text-xs font-black uppercase tracking-widest text-slate-400">Loading Scholar Standings...</p>
                    </div>
                ) : filteredStandings.length === 0 ? (
                    <div className="py-16 text-center">
                        <ScaleIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <h4 className="text-sm font-black text-slate-700 uppercase tracking-wider">No Scholars Found</h4>
                        <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                            {searchTerm ? 'No student matches your current search or filter criteria.' : 'No behavioral records logged for the selected period.'}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50/75 border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                    <th className="py-4 px-6 w-12 text-center">#</th>
                                    {renderSortHeader('Scholar', 'name', 'left')}
                                    <th className="py-4 px-6">Grade & Stream</th>
                                    {renderSortHeader('Merits', 'merits', 'center')}
                                    {renderSortHeader('Demerits', 'demerits', 'center')}
                                    {renderSortHeader('Net Standing', 'net', 'center')}
                                    <th className="py-4 px-6 text-center">Status</th>
                                    <th className="py-4 px-6 text-right">Breakdown</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 text-xs">
                                {filteredStandings.map((student, index) => {
                                    const isExpanded = expandedStudentId === student.student_id;
                                    const netScore = student.net_standing;
                                    const initials = (student.name || 'S')
                                        .split(' ')
                                        .map(n => n[0])
                                        .join('')
                                        .slice(0, 2)
                                        .toUpperCase();

                                    return (
                                        <React.Fragment key={student.student_id}>
                                            <tr
                                                onClick={() => toggleExpand(student.student_id)}
                                                className={`cursor-pointer transition-colors ${
                                                    isExpanded ? 'bg-indigo-50/40' : 'hover:bg-slate-50/70'
                                                }`}
                                            >
                                                <td className="py-4 px-6 text-center text-slate-400 font-bold text-[11px]">
                                                    {index + 1}
                                                </td>

                                                {/* Scholar Info */}
                                                <td className="py-4 px-6">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-9 h-9 rounded-xl bg-slate-100 text-[#18216D] font-black text-xs flex items-center justify-center shrink-0 border border-slate-200/50">
                                                            {initials}
                                                        </div>
                                                        <div>
                                                            <p className="font-black text-[#18216D] text-xs sm:text-sm">
                                                                {student.name}
                                                            </p>
                                                            <p className="text-[10px] font-bold text-slate-400 tracking-wider">
                                                                Adm: {student.student_adm || 'N/A'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* Grade & Stream */}
                                                <td className="py-4 px-6">
                                                    <span className="font-bold text-slate-700">
                                                        {student.grade || '—'}
                                                    </span>
                                                    {student.stream && (
                                                        <span className="text-slate-400 ml-1.5 font-medium text-[11px]">
                                                            ({student.stream})
                                                        </span>
                                                    )}
                                                </td>

                                                {/* Merits Awarded (No icon next to number) */}
                                                <td className="py-4 px-6 text-center">
                                                    {student.total_merits > 0 ? (
                                                        <span className="inline-flex items-center px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200/80 rounded-xl text-xs font-black">
                                                            +{student.total_merits}
                                                            <span className="text-[9px] text-emerald-600/70 font-bold ml-1">
                                                                ({student.merit_count})
                                                            </span>
                                                        </span>
                                                    ) : (
                                                        <span className="text-slate-300 font-bold">0</span>
                                                    )}
                                                </td>

                                                {/* Demerits Incurred (No icon next to number) */}
                                                <td className="py-4 px-6 text-center">
                                                    {student.total_demerits > 0 ? (
                                                        <span className="inline-flex items-center px-2.5 py-1 bg-rose-50 text-rose-700 border border-rose-200/80 rounded-xl text-xs font-black">
                                                            -{student.total_demerits}
                                                            <span className="text-[9px] text-rose-600/70 font-bold ml-1">
                                                                ({student.demerit_count})
                                                            </span>
                                                        </span>
                                                    ) : (
                                                        <span className="text-slate-300 font-bold">0</span>
                                                    )}
                                                </td>

                                                {/* Net Standing Score */}
                                                <td className="py-4 px-6 text-center">
                                                    <span className={`inline-block px-3 py-1 rounded-xl text-xs font-black shadow-sm ${
                                                        netScore > 0
                                                            ? 'bg-emerald-600 text-white shadow-emerald-600/20'
                                                            : netScore < 0
                                                            ? 'bg-rose-600 text-white shadow-rose-600/20'
                                                            : 'bg-slate-200 text-slate-700'
                                                    }`}>
                                                        {netScore > 0 ? `+${netScore}` : netScore}
                                                    </span>
                                                </td>

                                                {/* Status Badge */}
                                                <td className="py-4 px-6 text-center">
                                                    {getStatusBadge(student.status)}
                                                </td>

                                                {/* Actions & Expand Drilldown Caret */}
                                                <td className="py-4 px-6 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/teacher/student-detail/${student.student_id}?tab=character`, {
                                                                    state: { defaultTab: 'character', activeTab: 'character', returnUrl: window.location.pathname }
                                                                });
                                                            }}
                                                            className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-[#18216D] transition-colors"
                                                            title="View Student Profile"
                                                        >
                                                            <EyeIcon className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleExpand(student.student_id);
                                                            }}
                                                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-black transition-all ${
                                                                isExpanded
                                                                    ? 'bg-[#18216D] text-white shadow-sm'
                                                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                            }`}
                                                        >
                                                            <span>{isExpanded ? 'Hide' : 'View'}</span>
                                                            {isExpanded ? (
                                                                <ChevronUpIcon className="w-3.5 h-3.5 stroke-[3]" />
                                                            ) : (
                                                                <ChevronDownIcon className="w-3.5 h-3.5 stroke-[3]" />
                                                            )}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>

                                            {/* Expandable Accordion Sub-Panel (Table Design with 4 Columns) */}
                                            {isExpanded && (
                                                <tr className="bg-slate-50/80 border-y border-slate-200/60 animate-fadeIn">
                                                    <td colSpan={8} className="p-6">
                                                        <div className="bg-white rounded-2xl p-5 border border-slate-200/70 shadow-sm space-y-4">
                                                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-3">
                                                                <div className="flex items-center gap-2.5">
                                                                    <div className="w-2 h-2 rounded-full bg-[#3B82F6]" />
                                                                    <h5 className="text-xs font-black uppercase tracking-wider text-[#18216D]">
                                                                        Learning Area Breakdown for {student.name}
                                                                    </h5>
                                                                </div>
                                                                <div className="text-[11px] font-bold text-slate-400">
                                                                    Total Incidents Recorded: <span className="text-[#18216D] font-black">{student.total_incidents}</span>
                                                                </div>
                                                            </div>

                                                            {(!student.learning_area_breakdown || student.learning_area_breakdown.length === 0) ? (
                                                                <div className="py-6 text-center text-slate-400 text-xs font-medium italic">
                                                                    No specific learning area incidents recorded for this scholar during the selected term.
                                                                </div>
                                                            ) : (
                                                                <div className="overflow-x-auto rounded-xl border border-slate-100">
                                                                    <table className="w-full text-left border-collapse text-xs">
                                                                        <thead>
                                                                            <tr className="bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
                                                                                <th className="py-3 px-5">Learning Area Name</th>
                                                                                <th className="py-3 px-5 text-center">Merits</th>
                                                                                <th className="py-3 px-5 text-center">Demerits</th>
                                                                                <th className="py-3 px-5 text-center">Net Standing</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody className="divide-y divide-slate-100">
                                                                            {student.learning_area_breakdown.map((item, idx) => (
                                                                                <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                                                                                    <td className="py-3 px-5">
                                                                                        <p className="font-bold text-slate-800 text-xs">
                                                                                            {item.learning_area_name || item.course_name}
                                                                                        </p>
                                                                                        {item.course_name && item.course_name !== item.learning_area_name && (
                                                                                            <p className="text-[10px] font-medium text-slate-400 mt-0.5">
                                                                                                {item.course_name}
                                                                                            </p>
                                                                                        )}
                                                                                    </td>
                                                                                    <td className="py-3 px-5 text-center">
                                                                                        {item.merit_points > 0 ? (
                                                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-black bg-emerald-50 text-emerald-700 border border-emerald-100">
                                                                                                +{item.merit_points} pts
                                                                                                <span className="text-[9px] text-emerald-600/70 font-bold ml-1">
                                                                                                    ({item.merit_count})
                                                                                                </span>
                                                                                            </span>
                                                                                        ) : (
                                                                                            <span className="text-slate-300 font-bold">0</span>
                                                                                        )}
                                                                                    </td>
                                                                                    <td className="py-3 px-5 text-center">
                                                                                        {item.demerit_points > 0 ? (
                                                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-black bg-rose-50 text-rose-700 border border-rose-100">
                                                                                                -{item.demerit_points} pts
                                                                                                <span className="text-[9px] text-rose-600/70 font-bold ml-1">
                                                                                                    ({item.demerit_count})
                                                                                                </span>
                                                                                            </span>
                                                                                        ) : (
                                                                                            <span className="text-slate-300 font-bold">0</span>
                                                                                        )}
                                                                                    </td>
                                                                                    <td className="py-3 px-5 text-center">
                                                                                        <span className={`inline-block px-2.5 py-0.5 rounded-lg text-xs font-black ${
                                                                                            item.net_points > 0
                                                                                                ? 'bg-emerald-100 text-emerald-800'
                                                                                                : item.net_points < 0
                                                                                                ? 'bg-rose-100 text-rose-800'
                                                                                                : 'bg-slate-100 text-slate-600'
                                                                                        }`}>
                                                                                            {item.net_points > 0 ? `+${item.net_points}` : item.net_points}
                                                                                        </span>
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ScholarStandingRoster;
